import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Building2, Plus, HeartHandshake, Target } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

function IDR(n) {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);
}

const TYPE_LABEL = { PRIBADI: "Pribadi", JEMAAT: "Jemaat", LEMBAGA: "Lembaga" };

export default function PanitiaKantor() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ donor_name: "", amount: "", donor_type: "PRIBADI", church_id: null, date: new Date().toISOString().slice(0,10), notes: "" });

  const canAdd = ["SUPER_ADMIN","ADMIN_KLASIS","BENDAHARA_KLASIS","SEKRETARIS_KLASIS","KETUA_KLASIS"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    const [d, c] = await Promise.all([api.get("/donations"), api.get("/churches")]);
    setData(d.data); setChurches(c.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.donor_name || !form.amount) { toast.error("Nama donatur & jumlah wajib."); return; }
    try {
      await api.post("/donations", {
        ...form,
        amount: Number(form.amount),
        church_id: form.donor_type === "JEMAAT" ? form.church_id : null,
        notes: form.notes || null,
      });
      toast.success("Donasi tercatat.");
      setOpen(false);
      setForm({ donor_name: "", amount: "", donor_type: "PRIBADI", church_id: null, date: new Date().toISOString().slice(0,10), notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const total = data?.total || 0;
  const target = data?.target || 500_000_000;
  const pct = data?.progress_pct || 0;
  const remaining = Math.max(0, target - total);
  const yearsToTarget = 5;
  const targetPerYear = target / yearsToTarget;

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Panitia • RAKER 2026</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Panitia Pembangunan Kantor Klasis</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Prioritas strategis periode 2026–2031. Target pendanaan <strong>Rp 500.000.000</strong> dalam <strong>5 tahun</strong> (mulai 2027).
            Semua donasi tercatat, transparan, dan dapat diverifikasi.
          </p>
        </div>
        {canAdd && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-new-donation"><Plus className="h-4 w-4 mr-2" /> Catat Donasi</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Donasi Baru</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nama donatur</Label><Input value={form.donor_name} onChange={(e)=>setForm({...form, donor_name:e.target.value})} data-testid="input-donor-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Jenis</Label>
                    <Select value={form.donor_type} onValueChange={(v)=>setForm({...form, donor_type:v, church_id: null})}>
                      <SelectTrigger data-testid="select-donor-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(TYPE_LABEL).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Jumlah (Rp)</Label><Input type="number" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} data-testid="input-donor-amount" /></div>
                </div>
                {form.donor_type === "JEMAAT" && (
                  <div>
                    <Label>Jemaat</Label>
                    <Select value={form.church_id || ""} onValueChange={(v)=>setForm({...form, church_id:v})}>
                      <SelectTrigger data-testid="select-donor-church"><SelectValue placeholder="Pilih jemaat" /></SelectTrigger>
                      <SelectContent>
                        {churches.filter(c=>c.type==="JEMAAT").map((c)=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} data-testid="input-donor-date" /></div>
                <div><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></div>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="btn-save-donation">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : (
        <>
          {/* Progress banner */}
          <div className="relative overflow-hidden rounded-2xl bg-navy-gradient text-white p-8 mb-6 papua-motif">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              <div>
                <div className="text-[0.65rem] tracking-[0.22em] uppercase opacity-80 font-semibold">Target Pendanaan</div>
                <div className="font-heading font-bold text-4xl mt-2">{IDR(target)}</div>
                <div className="text-xs opacity-80 mt-1">≈ {IDR(targetPerYear)} per tahun</div>
              </div>
              <div>
                <div className="text-[0.65rem] tracking-[0.22em] uppercase opacity-80 font-semibold">Terkumpul</div>
                <div className="font-heading font-bold text-4xl mt-2 text-accent" data-testid="donations-total">{IDR(total)}</div>
                <div className="mt-2">
                  <Progress value={pct} className="h-2 bg-white/20" />
                </div>
                <div className="text-xs mt-1"><strong>{pct}%</strong> dari target</div>
              </div>
              <div>
                <div className="text-[0.65rem] tracking-[0.22em] uppercase opacity-80 font-semibold">Sisa</div>
                <div className="font-heading font-bold text-4xl mt-2">{IDR(remaining)}</div>
                <div className="text-xs opacity-80 mt-1">{data?.items?.length || 0} donasi tercatat</div>
              </div>
            </div>
          </div>

          {/* Info cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card><CardContent className="p-5">
              <Target className="h-5 w-5 text-primary mb-2" />
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Timeline</div>
              <div className="font-heading font-bold text-base mt-1">2027 – 2031</div>
              <div className="text-xs text-muted-foreground mt-1">Pembangunan mulai 2027 pasca RAKER</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <Building2 className="h-5 w-5 text-secondary mb-2" />
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Peruntukan</div>
              <div className="font-heading font-bold text-base mt-1">Kantor Klasis Mimika</div>
              <div className="text-xs text-muted-foreground mt-1">Pusat administrasi & pelayanan</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <HeartHandshake className="h-5 w-5 text-accent mb-2" />
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Dasar</div>
              <div className="font-heading font-bold text-base mt-1">Keputusan RAKER</div>
              <div className="text-xs text-muted-foreground mt-1">22 Agustus 2026 — GPDP El Olam</div>
            </CardContent></Card>
          </div>

          {/* Donations table */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Daftar Donatur</CardTitle></CardHeader>
            <CardContent className="p-0">
              {(!data?.items || data.items.length === 0) ? (
                <div className="p-12 text-center">
                  <HeartHandshake className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                  <div className="text-sm text-muted-foreground">Belum ada donasi tercatat. Klik "Catat Donasi" untuk memulai.</div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tanggal</TableHead>
                      <TableHead>Donatur</TableHead>
                      <TableHead>Jenis</TableHead>
                      <TableHead className="text-right">Jumlah</TableHead>
                      <TableHead>Catatan</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((d) => (
                      <TableRow key={d.id} data-testid={`donation-${d.id}`}>
                        <TableCell className="text-xs">{d.date}</TableCell>
                        <TableCell className="font-medium">{d.donor_name}</TableCell>
                        <TableCell><StatusBadge status="info" label={TYPE_LABEL[d.donor_type] || d.donor_type} /></TableCell>
                        <TableCell className="text-right font-mono text-primary font-semibold">{IDR(d.amount)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{d.notes || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </AppShell>
  );
}
